import React, { useState, useMemo, useEffect, useContext } from 'react';
import {
  MenuItem,
  Select,
  SimpleDialog,
  Typography,
  styled,
  Icons,
  Grid,
  TextField,
  InputAdornment,
  Divider,
  Button,
  CircularProgress,
} from '@nextiva/next-ui-components';
import clsx from 'clsx';
import { useImmerReducer } from 'use-immer';
import { orderBy, differenceWith, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useRootUserList } from 'customHooks/userCustomHooks';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { StyledSearchIcon } from 'components/SearchBar';
import { actionTypes as teamsActionTypes } from 'containers/pages/Teams/redux';

import { createTeam, updateTeamInfo } from 'api/iamOrchestrationApi';
import {
  bulkUpdateLicenseAssignment,
  getLicensesByUsers,
} from 'api/licenseService';
import { CREATE_TEAM, EDIT_TEAM } from 'constants/drawerTitles';
import { TEAMS_SUCCESS } from 'constants/routes';
import { useSnackbar, useTeamSMSLicense } from 'hooks';
import { getPhoneNumber, HuntGroupService } from 'services';
import { nameValidation } from 'utils/validators';
import { List } from './List';
import { UserChips } from './UserChips';
import { reducer } from './reducer';
import {
  CreateTeamContext,
  InitialState,
  actionTypes,
} from './createTeamContext';

const ModalMessage = styled(Typography)`
  margin: 24px 0px;
`;
const ModalAssignMessage = styled(Typography)`
  margin-bottom: 24px;
`;

const SelectComponent = styled(Select)`
  margin-bottom: 20px;
  width: 100%;
`;

export const CreateTeam = ({
  toggleDrawer = () => {},
  type = '',
  userRetrieveError = false,
  setNewlyCreatedTeam = () => {},
  editTeamInfo,
}) => {
  const queryClient = useQueryClient();
  const baseUserList = queryClient.getQueryData('getUsersBasicInfo');
  const [createTeamState, dispatch] = useImmerReducer(reducer, InitialState);
  const {
    corpAcctNbr,
    featureState: { authV3Enabled, teamsSMSLicensing },
  } = useContext(FeatureContext);
  const { availableTeamSMSLicenses } = useTeamSMSLicense();

  const [smsEnabled, setSmsEnabled] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(
    availableTeamSMSLicenses[0]?.id,
  );

  const getUserLicenses = () => {
    if (
      createTeamState.selectedUserList.length &&
      baseUserList &&
      type === EDIT_TEAM
    ) {
      const selectedUserUUIDs = baseUserList
        .filter((baseUser) =>
          createTeamState.selectedUserList.some(
            (user) => user.profileId === baseUser.profileId,
          ),
        )
        .map((baseUser) => baseUser.nextivaUUID)
        .join(',');
      getLicensesByUsers(corpAcctNbr, selectedUserUUIDs).then(
        ({ userProductFeaturesMap }) => {
          dispatch({
            type: actionTypes.SET_USERS_FEATURES,
            userProductFeaturesMap,
          });
        },
      );
    }
  };

  useEffect(() => {
    if (teamsSMSLicensing && type === EDIT_TEAM) {
      getUserLicenses();
    }
  }, [createTeamState.selectedUserList]);

  useEffect(() => {
    if (teamsSMSLicensing && editTeamInfo && type === EDIT_TEAM) {
      HuntGroupService.getHuntGroupAvailableNumbers(
        editTeamInfo.durableCallGroupId,
      ).then(({ primaryPhoneNumber }) => {
        if (primaryPhoneNumber) {
          getPhoneNumber(corpAcctNbr, primaryPhoneNumber).then((response) => {
            setSmsEnabled(response.data?.metadata.smsEnabled);
          });
        }
      });

      getUserLicenses();
    }
  }, []);

  const history = useHistory();
  const teamsDispatch = useDispatch();

  const [teamNameError, setTeamNameError] = useState(false);
  const [teamNameErrorMessage, setTeamNameErrorMessage] = useState('');
  const [searchDataValue, setSearchDataValue] = useState('');
  const [callFlowInfoText, setCallFlowInfoText] = useState('');
  const setSnackbar = useSnackbar(true);
  const { updateRootUserList } = useRootUserList();

  const contextValue = useMemo(
    () => ({
      createTeamState,
      smsEnabled,
      toggleDrawer,
      editTeamInfo,
      dispatch,
    }),
    [createTeamState, smsEnabled, dispatch],
  );

  const proceedToLicense = () => {
    dispatch({
      type: actionTypes.SET_NO_LICENSE_MODAL,
      payload: false,
    });
    history.push('/licensing');
  };

  const assignTeamSMSLicense = () => {
    dispatch({ type: actionTypes.SET_SENDING, payload: true });
    dispatch({
      type: actionTypes.SET_MULTI_NO_LICENSE_MODAL,
      payload: '',
    });
    dispatch({
      type: actionTypes.SET_ASSIGN_LICENSE_MODAL,
      payload: '',
    });
    const licenseDefinition = availableTeamSMSLicenses.find(
      (lic) => lic.id === selectedLicense,
    )?.licenseDefinition;

    const licenseName = licenseDefinition.name.includes('NUC-')
      ? licenseDefinition.description
      : licenseDefinition.name;

    bulkUpdateLicenseAssignment(
      selectedLicense,
      createTeamState.assignLicenseBody,
      authV3Enabled,
      corpAcctNbr,
    )
      .then(() => {
        getUserLicenses();
        setSnackbar({
          message: `${licenseName} was successfully assigned`,
          severity: 'success',
        });
        queryClient.invalidateQueries(['licenseList']);
        queryClient.invalidateQueries(['getUserList']);
        dispatch({ type: actionTypes.SET_SENDING, payload: false });
        setTimeout(toggleDrawer, 4000);
      })
      .catch(() => {
        setSnackbar({
          message: `There was an error assigning the ${licenseName} license`,
          severity: 'error',
        });
        dispatch({ type: actionTypes.SET_SENDING, payload: false });
        setTimeout(toggleDrawer, 4000);
      });
  };

  const determineHasCallFlows = async () => {
    if (type === CREATE_TEAM) return;

    try {
      const {
        numberOfCallFlows,
      } = await HuntGroupService.getCallFlowCountForTeam(editTeamInfo?.id);

      if (numberOfCallFlows > 0) {
        setCallFlowInfoText(
          'Any updates that you make here will also update any call flows that are currently using this team.',
        );
      }
    } catch {
      setCallFlowInfoText(
        'Sorry, we could not verify if this team is being used by any call flows. If you are not sure either, you should double check before you make changes.',
      );
    }
  };

  const setUserInfo = () => {
    const sortedUserList = orderBy(baseUserList, ['firstName'], ['asc']);

    dispatch({
      type: actionTypes.SET_USER_LISTS,
      baseUserList,
      userList: sortedUserList,
    });
  };

  const setTeamInfo = () => {
    let profileIdsToSet = [];

    if (type === EDIT_TEAM && editTeamInfo?.assignedProfileIds) {
      profileIdsToSet = editTeamInfo.assignedProfileIds;

      dispatch({
        type: actionTypes.SET_EDIT_TEAM_CHECKED_USERS,
        assignedProfiles: profileIdsToSet,
      });
    }

    dispatch({
      type: actionTypes.SET_TEAM_INFO,
      payload: {
        initialProfileIds: profileIdsToSet,
        teamName: type === CREATE_TEAM ? '' : editTeamInfo?.name,
        voiceEnabled: type === CREATE_TEAM ? false : editTeamInfo?.voiceEnabled,
      },
    });
  };

  const searchData = (e) => {
    setSearchDataValue(e.target.value);
    dispatch({
      type: actionTypes.FILTER_USER_LIST_BY_SEARCH,
      searchString: e.target.value,
    });
  };

  const handleInputChange = (e) => {
    const { value, id } = e.target;

    dispatch({
      type: actionTypes.SET_TEAM_INFO,
      payload: { [id]: value },
    });

    const isValidName = nameValidation(value);
    setTeamNameError(!isValidName.isValid);
    setTeamNameErrorMessage(isValidName.error);
  };

  const invalidateCaches = async () => {
    await queryClient.invalidateQueries(['getTeamsOptions']);
    await queryClient.invalidateQueries(['getTeams']);
    await queryClient.invalidateQueries(['getUsersBasicInfo']);
  };

  const saveCreate = async (profileIds) => {
    const createTeamPayload = {
      addedProfileIds: profileIds,
      name: createTeamState.teamInfo.teamName.trim(),
    };

    try {
      const res = await createTeam(createTeamPayload);
      dispatch({ type: actionTypes.SET_SENDING, payload: false });

      const teamProfileIds = res.assignedProfileIds ?? [];

      teamsDispatch({
        type: teamsActionTypes.ADD_TEAM_TO_LIST,
        teamInfo: {
          ...res,
          teamName: res.name,
          initialProfileIds: teamProfileIds,
        },
      });

      setNewlyCreatedTeam({
        assignedProfiles: teamProfileIds,
        teamId: res.id,
        name: res.name,
      });

      toggleDrawer();
      history.push(TEAMS_SUCCESS);
      updateRootUserList();

      // Await cache invalidation
      await invalidateCaches();
    } catch (err) {
      dispatch({ type: actionTypes.SET_SENDING, payload: false });
      if (
        err?.message.includes('name must be unique') ||
        err?.message.includes('The role already exists')
      ) {
        setSnackbar({
          message: 'A team with that name already exists.',
          severity: 'error',
        });
      } else {
        setSnackbar({
          message:
            'Unable to create team. Please try again later or contact support if the issue persists.',
          severity: 'error',
        });
      }
    }
  };

  const saveEdit = async (profileIds) => {
    const droppedUsers = differenceWith(
      createTeamState.teamInfo.initialProfileIds,
      profileIds,
      isEqual,
    );

    const newUsers = differenceWith(
      profileIds,
      createTeamState.teamInfo.initialProfileIds,
      isEqual,
    );

    const editTeamPayload = {
      addedProfileIds: newUsers,
      droppedProfileIds: droppedUsers,
      name: createTeamState.teamInfo.teamName.trim(),
      voiceEnabled: createTeamState.teamInfo.voiceEnabled,
    };

    try {
      await updateTeamInfo(editTeamInfo?.name.trim(), editTeamPayload);
      dispatch({ type: actionTypes.SET_SENDING, payload: false });
      updateRootUserList();
      setSnackbar({
        message: 'Team was successfully updated.',
        severity: 'success',
      });

      teamsDispatch({
        type: teamsActionTypes.UPDATE_TEAM,
        id: editTeamInfo?.id,
        teamInfo: {
          assignedProfileIds: profileIds,
          name: createTeamState.teamInfo.teamName,
          userCount: createTeamState.selectedUserList.length,
        },
      });
      await invalidateCaches();
      setTimeout(toggleDrawer, 4000);
    } catch (error) {
      dispatch({ type: actionTypes.SET_SENDING, payload: false });
      if (
        error?.message.includes('name must be unique') ||
        error?.message.includes(' Role with name and scope already exists')
      ) {
        setSnackbar({
          message: 'A team with that name already exists.',
          severity: 'error',
        });
      } else if (
        error?.message.includes(
          'Either location id or hunt group ids is required',
        )
      ) {
        setSnackbar({
          message: 'Either location id or hunt group ids is required.',
          severity: 'error',
        });
      } else {
        setSnackbar({ message: error.message, severity: 'error' });
      }
    }
  };

  const onSubmit = () => {
    dispatch({ type: actionTypes.SET_SENDING, payload: true });

    const profileIds = createTeamState.selectedUserList.map((user) => {
      return user.profileId;
    });

    if (type === EDIT_TEAM) {
      saveEdit(profileIds);
    } else if (type === CREATE_TEAM) {
      saveCreate(profileIds);
    }
  };

  useEffect(() => {
    setUserInfo();
    setTeamInfo();
  }, [type, editTeamInfo]);

  useEffect(() => {
    if (userRetrieveError) {
      setSnackbar({
        message:
          'Unable to retrieve Users. Please try again later or contact support if the issue persists.',
        severity: 'error',
      });
    }
  }, [baseUserList]);

  useEffect(() => {
    if (createTeamState.userList.length !== 0) {
      dispatch({
        type: actionTypes.SET_IS_LOADING,
        isLoading: false,
      });
    }
  }, [createTeamState.userList]);

  useEffect(() => {
    determineHasCallFlows();
  }, [editTeamInfo?.id]);

  const disabled =
    createTeamState.teamInfo.teamName?.length < 1 || teamNameError;

  return (
    <div className="m-create-team">
      <CreateTeamContext.Provider value={contextValue}>
        {callFlowInfoText.length > 0 && (
          <Grid item xs={12}>
            <div
              style={{
                padding: '16px',
                margin: '0 0 24px',
                borderRadius: '4px',
                background: '#edf4f7',
              }}
            >
              {callFlowInfoText}
            </div>
          </Grid>
        )}

        <Grid container spacing={0}>
          <Grid item xs={12}>
            <div className="m-create-team__team-info">
              <TextField
                id="teamName"
                fullWidth
                helperText={teamNameErrorMessage}
                label="Team Name"
                maxLength={30}
                error={teamNameError}
                value={createTeamState.teamInfo.teamName}
                onChange={handleInputChange}
                data-testid="PlatformAdminUI-CreateTeam-TextField"
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <div className="m-create-team__team-info">
              <Divider
                orientation="horizontal"
                variant="fullWidth"
                data-testid="PlatformAdminUI-TeamsLink-CreateTeam-Divider"
              />
            </div>
          </Grid>

          <Grid item xs={12}>
            <div className="m-create-team__user-chips">
              <UserChips />
            </div>
          </Grid>

          <Grid item xs={12}>
            <div className="m-create-team__search">
              <div
                className={clsx('Heading3', 'm-create-team__search__title')}
                data-testid="PlatformAdminUI-TeamsLink-CreateTeam-SearchTitle"
              >
                Search and assign users to this team
              </div>
              <div className="m-create-team__search__search">
                <TextField
                  type="search"
                  id="search"
                  fullWidth
                  placeholder="Search users"
                  value={searchDataValue}
                  onChange={searchData}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <StyledSearchIcon icon={Icons.search} />
                      </InputAdornment>
                    ),
                  }}
                  data-testid="PlatformAdminUI-TeamsLink-CreateTeam-SearchField"
                />
              </div>
              <div
                className={clsx(
                  'Caption',
                  'm-create-team__search__current-users_text',
                )}
                data-testid="PlatformAdminUI-TeamsLink-CreateTeam-CurrentUsersText"
              >
                <span className="m-create-team__search__current-users_text--accent">
                  {createTeamState.selectedUserList.length}
                </span>{' '}
                {createTeamState.selectedUserList?.length === 1
                  ? 'user is'
                  : 'users are'}{' '}
                currently assigned to this team
              </div>
            </div>
          </Grid>
          <Grid item xs={12}>
            <div
              className="m-create-team__user-table"
              data-testid="PlatformAdminUI-TeamsLink-CreateTeam-UserTable"
            >
              <List />
            </div>
            <SimpleDialog
              cancelButtonProps={{
                onClick: () => {
                  dispatch({
                    type: actionTypes.SET_NO_LICENSE_MODAL,
                    payload: false,
                  });
                },
                'data-testid': 'PlatformAdminUI-List-NoLicenseCancelButton',
              }}
              cancelText="Cancel"
              confirmButtonProps={{
                color: 'primary',
                onClick: proceedToLicense,
                'data-testid': 'PlatformAdminUI-List-NoLicenseButton',
              }}
              confirmText="Yes, go to licensing"
              title="No available licenses"
              maxWidth="xs"
              open={createTeamState.noLicenseModal}
            >
              <ModalMessage variant="body2">
                There are no additional licenses available to assign to this
                user.
              </ModalMessage>
              <ModalMessage variant="body2">
                Would you like to be redirected to licensing administration to
                manage your licenses?
              </ModalMessage>
            </SimpleDialog>
            <SimpleDialog
              cancelButtonProps={{
                onClick: () => {
                  dispatch({
                    type: actionTypes.SET_ASSIGN_LICENSE_MODAL,
                    payload: '',
                  });
                },
                'data-testid': 'PlatformAdminUI-List-AssignLicenseCancelButton',
              }}
              cancelText="Cancel"
              confirmButtonProps={{
                color: 'primary',
                onClick: assignTeamSMSLicense,
                'data-testid': 'PlatformAdminUI-List-AssignLicenseButton',
              }}
              confirmText="Yes, assign license"
              title="Enable Message Pro?"
              maxWidth="xs"
              open={!!createTeamState.assignLicenseModal}
            >
              <ModalAssignMessage variant="body2">
                This will assign a Message Pro license to{' '}
                {createTeamState.assignLicenseModal}.
              </ModalAssignMessage>
              <ModalMessage variant="body2">
                Are you sure you want to assign a license to this user?
              </ModalMessage>
            </SimpleDialog>
            <SimpleDialog
              cancelButtonProps={{
                onClick: () => {
                  dispatch({
                    type: actionTypes.SET_MULTI_NO_LICENSE_MODAL,
                    payload: '',
                  });
                },
                'data-testid':
                  'PlatformAdminUI-List-MultiAssignLicenseCancelButton',
              }}
              cancelText="Cancel"
              confirmButtonProps={{
                color: 'primary',
                onClick: assignTeamSMSLicense,
                'data-testid': 'PlatformAdminUI-List-MultiAssignLicenseButton',
              }}
              confirmText="Assign license"
              title="Enable Message Pro?"
              maxWidth="xs"
              open={!!createTeamState.multiLicenseModal}
            >
              <ModalAssignMessage variant="body2">
                To enable Message Pro for {createTeamState.multiLicenseModal}{' '}
                you must assign them a Message Pro license.
                <br />
                <br />
                Please select which license you would like to assign.
              </ModalAssignMessage>
              <SelectComponent
                labelId="multiLicense"
                value={selectedLicense}
                onChange={(event) => setSelectedLicense(event.target.value)}
                placeholder={
                  availableTeamSMSLicenses[0]?.licenseDefinition.name.includes(
                    'NUC-',
                  )
                    ? availableTeamSMSLicenses[0].licenseDefinition.description
                    : availableTeamSMSLicenses[0]?.licenseDefinition.name
                }
                data-testid="PlatformAdminUI-CreateTeam-SelectLicense"
              >
                {availableTeamSMSLicenses.map((license) => (
                  <MenuItem key={license.id} value={license.id}>
                    {license.licenseDefinition.name.includes('NUC-')
                      ? license.licenseDefinition.description
                      : license.licenseDefinition.name}
                  </MenuItem>
                ))}
              </SelectComponent>
            </SimpleDialog>
          </Grid>

          <div className="m-drawer-footer">
            <div className="m-drawer-footer__container">
              <div className="m-drawer-footer__container__action-bar">
                <div className="m-drawer-footer__container__action-bar__button">
                  <Button
                    className="footer__btn"
                    onClick={toggleDrawer}
                    fullWidth
                    color="secondary"
                    variant="contained"
                    data-testid="PlatformAdminUI-TeamsLink-CreateTeam-CancelButton"
                  >
                    Cancel
                  </Button>
                </div>
                <div className="m-drawer-footer__container__action-bar__button">
                  <Button
                    fullWidth
                    color={disabled ? 'secondary' : 'primary'}
                    variant="contained"
                    onClick={onSubmit}
                    className="footer__btn"
                    disabled={disabled}
                    data-testid="PlatformAdminUI-TeamsLink-CreateTeam-CreateButton"
                  >
                    {type === CREATE_TEAM && 'Create'}
                    {type === EDIT_TEAM && 'Save'}
                  </Button>
                </div>
                {createTeamState.sending && (
                  <div className="m-drawer-footer__container__action-bar__spinner">
                    <CircularProgress
                      style={{ color: '#009FE0' }}
                      variant="indeterminate"
                      size={36}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Grid>
      </CreateTeamContext.Provider>
    </div>
  );
};

CreateTeam.propTypes = {
  editTeamInfo: PropTypes.object.isRequired,
  setNewlyCreatedTeam: PropTypes.func.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  userRetrieveError: PropTypes.bool,
};
